import { useCallback, useEffect, useRef } from "react";

// Native HTML5 drag does not scroll the container it hovers, so a long board column is
// unreachable mid-drag: the pointer parks at the edge and nothing moves.
const EDGE_ZONE_PX = 72;
const MAX_STEP_PX = 18;

// Full speed at the very edge, easing to a stop at the end of the zone.
function stepFor(distanceToEdge: number): number {
  const closeness = (EDGE_ZONE_PX - distanceToEdge) / EDGE_ZONE_PX;
  return Math.ceil(closeness * MAX_STEP_PX);
}

export function useDragAutoScroll() {
  const scrollerRef = useRef<HTMLElement | null>(null);
  const stepRef = useRef(0);
  const frameRef = useRef<number | null>(null);

  const stopAutoScroll = useCallback(() => {
    stepRef.current = 0;
    if (frameRef.current !== null) {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    }
  }, []);

  const runAutoScroll = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller || stepRef.current === 0) {
      frameRef.current = null;
      return;
    }
    scroller.scrollTop += stepRef.current;
    frameRef.current = requestAnimationFrame(runAutoScroll);
  }, []);

  const handleDragOver = useCallback(
    (event: React.DragEvent<HTMLElement>) => {
      // A container whose dragover does not preventDefault cannot be dropped on; keep that here
      // rather than relying on an ancestor handler doing it.
      event.preventDefault();
      const scroller = event.currentTarget;
      const bounds = scroller.getBoundingClientRect();
      const distanceFromTop = event.clientY - bounds.top;
      const distanceFromBottom = bounds.bottom - event.clientY;

      scrollerRef.current = scroller;
      if (distanceFromTop < EDGE_ZONE_PX) {
        stepRef.current = -stepFor(distanceFromTop);
      } else if (distanceFromBottom < EDGE_ZONE_PX) {
        stepRef.current = stepFor(distanceFromBottom);
      } else {
        stepRef.current = 0;
      }

      if (stepRef.current !== 0 && frameRef.current === null) {
        frameRef.current = requestAnimationFrame(runAutoScroll);
      }
    },
    [runAutoScroll],
  );

  useEffect(() => stopAutoScroll, [stopAutoScroll]);

  return { handleDragOver, stopAutoScroll };
}
